import { ColDef, ColGroupDef, GridApi } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { OverlayService } from '@shared/services/overlay.service';
import { measureText } from '@shared/utils';

import { ColumnChooserModalComponent } from './components/column-chooser-modal/column-chooser-modal.component';
import {
  ColumnChooserModel,
  ColumnChooserNode,
} from './components/column-chooser-modal/model/column-chooser.model';
import {
  CustomChooserModel,
  CustomMapper,
} from './components/column-chooser-modal/model/custom-chooser.model';
import { ColumnChooserService } from './services/column-chooser.service';
import { ButtonComponent } from '../button/button.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, IconComponent, TooltipDirective],
  selector: 'aux-column-chooser',
  standalone: true,
  styleUrl: './column-chooser.component.scss',
  templateUrl: './column-chooser.component.html',
})
export class ColumnChooserComponent<T> {
  private overlayService = inject(OverlayService);

  isColumnChooserApplied = signal(false);
  pinnedColumnCount = signal(0);

  showPinIcons = input(false);

  showThereArePinnedColumnsIndicator = computed(() => {
    return this.pinnedColumnCount() > 0 && this.showPinIcons();
  });

  columnMapper = input<Record<string, CustomMapper>>();
  configKey = input.required<string>();
  disabled = input(false);
  excludeColumns = input<string[]>([]);
  gridApi = input.required<GridApi<T>>();
  oneColumnWidthStrategy = input<Record<string, number>>({});
  reorder = input(false);
  validations = input<((columns: ColumnChooserNode[]) => string | undefined)[]>([]);

  afterColumnChooserApplied = output<void>();

  columnChooserService!: ColumnChooserService;

  private readonly AT_LEAST_ONE_COLUMN = 'You must select at least one column';
  private originalColumnWidth: Record<string, number> = {};

  adjustColumnWidths(config: ColumnChooserNode[]) {
    const originalColumnWidth = Object.entries(this.originalColumnWidth);

    if (originalColumnWidth.length) {
      originalColumnWidth.forEach(([key, width]) => {
        this.gridApi().setColumnWidth(key, width);
      });

      this.originalColumnWidth = {};
    }

    config
      .filter((col) => !!col.children?.length)
      .forEach((col) => {
        const selectedColumns = col.children?.filter((col) => col.checked);

        if (col.children && selectedColumns?.length === 1) {
          const nestedColumnField = selectedColumns[0].field;
          const headerName = this.gridApi().getColumn(nestedColumnField)?.getColDef().headerName;

          const actualWidth = this.gridApi().getColumn(nestedColumnField)?.getActualWidth();

          if (!headerName) return;

          const nestedHeaderWidth = measureText(headerName);
          const parentHeaderWidth = measureText(col.headerName);

          const PADDING = 100;

          const headerWidth = Math.max(nestedHeaderWidth, parentHeaderWidth) + PADDING;
          const adjustColumnStrategy = this.oneColumnWidthStrategy();
          const extraWidth =
            adjustColumnStrategy[nestedColumnField] || adjustColumnStrategy[col.field] || 0;

          if (actualWidth) {
            this.originalColumnWidth[nestedColumnField] = actualWidth;
          }

          this.gridApi().setColumnWidth(nestedColumnField, headerWidth + extraWidth);
        }
      });
  }

  applyColumnChooser() {
    const applied = this.columnChooserService.applyColumnChooser(
      this.configKey(),
      this.reorder(),
      this.excludeColumns()
    );

    this.pinnedColumnCount.set(
      this.columnChooserService.getPinnedColumnCount(
        this.columnChooserService.getTypeSafeColumnChooserConfig(
          this.configKey(),
          this.excludeColumns()
        )
      )
    );

    this.afterColumnChooserApplied.emit();

    return applied;
  }

  constructor() {
    effect(() => {
      if (this.gridApi()) {
        this.columnChooserService = new ColumnChooserService(this.gridApi());

        const hasColumnChooserConfig = this.columnChooserService.hasColumnChooserConfig(
          this.configKey()
        );

        if (!hasColumnChooserConfig) return;

        const config = this.columnChooserService.getTypeSafeColumnChooserConfig(
          this.configKey(),
          untracked(() => this.excludeColumns())
        );

        const isAllColumnsSelected = ColumnChooserModel.areAllColumnsSelected(config);
        const areSomeColumnsPinned = ColumnChooserModel.areSomeColumnsPinned(config);

        if (!isAllColumnsSelected || areSomeColumnsPinned) {
          const applied = this.applyColumnChooser();

          this.adjustColumnWidths(config);

          this.isColumnChooserApplied.set(applied);
        }
      }
    });
  }

  private filterNestedChildren(colGroup: ColGroupDef): (ColDef | ColGroupDef)[] {
    return colGroup.children.filter((child) => {
      if (this.isColGroup(child)) {
        // Recursively filter this group's children
        const nestedChildren = this.filterNestedChildren(child);

        child.children = nestedChildren;

        return nestedChildren.length > 0;
      } else {
        const childId = (child as ColDef<T>).field;

        return !this.excludeColumns().includes(<string>childId);
      }
    });
  }

  private getColumnDefs() {
    const columnDefs = this.gridApi()?.getColumnDefs() || [];

    return columnDefs.filter((col) => {
      const id = this.isColGroup(col) ? col.groupId : (col as ColDef<T>).field;

      // Check if the column itself is excluded
      if (this.excludeColumns().includes(<string>id)) {
        return false;
      }

      // If it's a column group, filter its children and check if any remain
      if (this.isColGroup(col)) {
        // Create a filtered copy of children, removing excluded columns
        const filteredChildren = col.children.filter((child) => {
          const childId = (child as ColDef<T>).field;

          return !this.excludeColumns().includes(<string>childId);
        });

        col.children = filteredChildren.map((child) => {
          if (this.isColGroup(child)) {
            const nestedChildren = this.filterNestedChildren(child);

            return { ...child, children: nestedChildren };
          }

          return child;
        });

        // Only include groups that have at least one child remaining
        return col.children.length > 0;
      }

      return true;
    });
  }

  private isColGroup(cell: ColDef | ColGroupDef): cell is ColGroupDef {
    return !!(cell as ColGroupDef)?.children;
  }

  // eslint-disable-next-line max-lines-per-function
  onOpenColumnChooser() {
    const columnDefs = this.getColumnDefs();

    const chooserService = new ColumnChooserService(this.gridApi());
    const hasConfig = chooserService.hasColumnChooserConfig(this.configKey());
    let columnsInstance: ColumnChooserModel | CustomChooserModel;

    if (hasConfig) {
      // Get saved config and merge with current grid configuration
      const savedConfig = chooserService.getTypeSafeColumnChooserConfig(
        this.configKey(),
        this.excludeColumns()
      );

      const mergedConfig = chooserService.mergeConfigWithCurrentGrid(
        savedConfig,
        this.excludeColumns()
      );

      if (this.columnMapper()) {
        // For custom chooser, we need to apply the custom mapping logic
        const customModel = new CustomChooserModel([], this.columnMapper()!);

        customModel.columns = mergedConfig;
        // Re-apply the custom mapping logic
        customModel.reapplyCustomMapping();
        columnsInstance = customModel;
      } else {
        columnsInstance = Object.assign(new ColumnChooserModel([]), { columns: mergedConfig });
      }
    } else {
      // Fallback to building from columnDefs
      columnsInstance = this.columnMapper()
        ? new CustomChooserModel(columnDefs, this.columnMapper()!)
        : new ColumnChooserModel(columnDefs);
    }

    this.overlayService.openPopup({
      content: ColumnChooserModalComponent,
      data: {
        columns: columnsInstance,
        reorder: this.reorder(),
        showPinIcons: this.showPinIcons(),
      },
      settings: {
        header: 'Select your columns',
        panelClasses: ['column-chooser-modal'],
        primaryButton: {
          action: (instance: ColumnChooserModalComponent | null) => {
            const columnChooserService = new ColumnChooserService(this.gridApi());

            let columns = instance?.chooser()?.mutableColumns() || [];

            if (columnsInstance instanceof CustomChooserModel) {
              columns = columnsInstance.restoreChildrenToMappedColumns(columns);
            }

            columnChooserService.saveColumnChooser(columns, this.configKey());
            const applied = this.applyColumnChooser();

            this.isColumnChooserApplied.set(
              ColumnChooserModel.areAllColumnsSelected(columns) ? false : applied
            );

            this.adjustColumnWidths(columns);

            instance?.ref.close();
          },
          disabled: (instance) => {
            const columns = instance?.chooser()?.mutableColumns() || [];
            const customValidations = this.validations();

            const isInvalid = customValidations.some((fn) => !!fn(columns));

            return !ColumnChooserService.atLeastOneColumnSelected(columns) || isInvalid;
          },
          label: 'Apply',
          tooltip: (instance) => {
            const errors: string[] = [];
            const columns = instance?.chooser()?.mutableColumns() || [];
            const customValidations = this.validations();

            customValidations.forEach((fn) => {
              const error = fn(columns);

              if (error) {
                errors.push(error);
              }
            });

            if (!ColumnChooserService.atLeastOneColumnSelected(columns)) {
              errors.push(this.AT_LEAST_ONE_COLUMN);
            }

            return errors;
          },
        },
        secondaryButton: {
          hidden: true,
        },
      },
    });
  }
}
